import { INSTAGRAM_AUTH_CODE_KEY, TIKTOK_AUTH_CODE_KEY } from '@blocs.auth';
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  SocialMediaPlatform,
  ConnectSocialMediaDoc,
  DisconnectSocialMediaDoc,
  DeleteSocialMediaDataDoc,
  SocialMedia,
} from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { getEnvironment } from '@minecraft.environment';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { captureExceptionEvent } from '@blocs.sentry';
import { useDeviceAndBrowserInfo } from '@minecraft.utils';
import { useGoogleLogin } from '@react-oauth/google';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 800;
const AUTH_TIMEOUT = 120000;

const SOCIAL_MEDIA_FIELD_MAP = {
  [SocialMediaPlatform.Instagram]: { field: 'instagram', platformName: 'Instagram' },
  [SocialMediaPlatform.Youtube]: { field: 'youtube', platformName: 'YouTube' },
  [SocialMediaPlatform.Tiktok]: { field: 'tiktok', platformName: 'TikTok' },
};

const SUPPORTED_SOCIAL_MEDIA_PLATFORMS = Object.keys(SOCIAL_MEDIA_FIELD_MAP) as SocialMediaPlatform[];

const getPopupPosition = (): { top: number; left: number } => {
  const wLeft = window.screenLeft ?? window.screenX;
  const wTop = window.screenTop ?? window.screenY;
  return {
    left: wLeft + (window.innerWidth - POPUP_WIDTH) / 2,
    top: wTop + (window.innerHeight - POPUP_HEIGHT) / 2,
  };
};

const getPopupDimensions = (): string => {
  const { top, left } = getPopupPosition();
  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},top=${top},left=${left}`;
};

const useSocialMediaAuth = (platform: 'instagram' | 'tiktok') => {
  const getAuthCodeKey = () => {
    switch (platform) {
      case 'instagram':
        return INSTAGRAM_AUTH_CODE_KEY;
      case 'tiktok':
        return TIKTOK_AUTH_CODE_KEY;
      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  };

  const getErrorMessage = (errorType: string) => {
    switch (errorType) {
      case 'timeout':
        return `${platform.charAt(0).toUpperCase() + platform.slice(1)} auth timed out after 2 minutes`;
      case 'cancelled':
        return 'Authentication cancelled - popup closed';
      case 'failed':
        return `${platform.charAt(0).toUpperCase() + platform.slice(1)} auth failed`;
      default:
        return 'Authentication failed';
    }
  };

  const onAuthCodeReceived = useCallback(
    async (popup: Window | null): Promise<string | null> => {
      if (!popup) {
        console.error('Popup window failed to open');
        return null;
      }
      const channel = new BroadcastChannel(getAuthCodeKey());
      const cleanup = (intervalId?: NodeJS.Timeout) => {
        channel.close();
        if (intervalId) clearInterval(intervalId);
      };
      try {
        return await new Promise((resolve, reject) => {
          // Check if popup is closed periodically
          const popupCheckInterval = setInterval(() => {
            if (popup == null) {
              cleanup(popupCheckInterval);
              reject(new Error(getErrorMessage('cancelled')));
            }
          }, 1000);

          // Set timeout for auth
          const timeoutId = setTimeout(() => {
            cleanup(popupCheckInterval);
            reject(new Error(getErrorMessage('timeout')));
          }, AUTH_TIMEOUT);

          channel.onmessage = (event: MessageEvent) => {
            // Validate message origin
            if (event.origin !== window.location.origin) {
              console.warn('Received message from unauthorized origin:', event.origin);
              return;
            }
            const { type, code, error } = event.data;

            // Clear timers and close channel before handling response
            cleanup(popupCheckInterval);
            clearTimeout(timeoutId);
            if (type === 'error') {
              reject(new Error(`${getErrorMessage('failed')}: ${error}`));
              return;
            }
            if (type !== 'success') {
              reject(new Error('Invalid response type received'));
              return;
            }
            if (!code || typeof code !== 'string') {
              reject(new Error('Invalid or missing auth code'));
              return;
            }
            resolve(code);
          };
        });
      } catch (error) {
        cleanup();
        throw error;
      }
    },
    [platform]
  );
  return { onAuthCodeReceived };
};

interface UseSocialMediaActionsProps {
  profileId: string;
  accountId?: string;
  artistId?: string;
  heapLocation?: 'social-upgrade' | 'submission-page';
  existingSocialMedia: SocialMedia;
}

export const useSocialMediaActions = ({
  profileId,
  accountId,
  artistId,
  heapLocation,
  existingSocialMedia,
}: UseSocialMediaActionsProps) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  const { onAuthCodeReceived: onInstagramAuthCodeReceived } = useSocialMediaAuth('instagram');
  const { onAuthCodeReceived: onTikTokAuthCodeReceived } = useSocialMediaAuth('tiktok');
  const [connectSocialMedia] = useMutation(ConnectSocialMediaDoc);
  const [disconnectSocialMedia] = useMutation(DisconnectSocialMediaDoc);
  const [deleteSocialMediaData] = useMutation(DeleteSocialMediaDataDoc);
  const [mutationLoading, setMutationLoading] = useState(false);

  const handleConnectSocialMediaError = () => {
    showMessage({ color: 'danger', message: t('talent:profileEdit.social.connect.error') });
  };

  const handleSocialMediaAction = async ({
    action,
    socialMediaPlatform,
  }: {
    action: 'disconnect' | 'delete';
    socialMediaPlatform: SocialMediaPlatform;
  }) => {
    try {
      if (!SUPPORTED_SOCIAL_MEDIA_PLATFORMS.includes(socialMediaPlatform)) {
        return;
      }

      setMutationLoading(true);
      const mutation = action === 'disconnect' ? disconnectSocialMedia : deleteSocialMediaData;
      const response = await mutation({
        variables: {
          profileId: Number(profileId),
          socialMediaPlatform,
        },
        update: (cache, { data, errors }) => {
          if ((!data?.disconnectSocialMedia?.success && !data?.deleteSocialMediaData?.success) || errors) return;
          cache.modify({
            id: cache.identify({ __typename: 'Profile', id: Number(profileId) }),
            fields: {
              socialMedia: () => ({
                ...existingSocialMedia,
                id: Number(profileId),
                __typename: 'SocialMedia',
                [SOCIAL_MEDIA_FIELD_MAP[socialMediaPlatform].field]: null,
              }),
            },
          });
        },
      });

      let success = false;
      if (action === 'disconnect') {
        if ('disconnectSocialMedia' in response.data) {
          success = response.data.disconnectSocialMedia?.success || false;
        }
      } else if ('deleteSocialMediaData' in response.data) {
        success = response.data.deleteSocialMediaData?.success || false;
      }

      if (!success) {
        throw new Error(`Failed to ${action} social media`);
      }
      showMessage({
        color: 'success',
        message: t('talent:profileEdit.social.disconnect.successful', {
          socialMediaPlatform: SOCIAL_MEDIA_FIELD_MAP[socialMediaPlatform].platformName,
        }),
      });
    } catch (error) {
      captureExceptionEvent(`${socialMediaPlatform} account ${action} failed`, {
        location: `talent > profile > social ${action} modal`,
        profileId,
        accountId,
        artistId,
        errorMessage: error?.message,
        errorDescription: error?.description,
      });
      showMessage({ color: 'danger', message: t('talent:profileEdit.social.connect.error') });
    } finally {
      setMutationLoading(false);
    }
  };

  const disconnectSocialAccount = ({ socialMediaPlatform }: { socialMediaPlatform: SocialMediaPlatform }) =>
    handleSocialMediaAction({ action: 'disconnect', socialMediaPlatform });

  const deleteAllSocialData = ({ socialMediaPlatform }: { socialMediaPlatform: SocialMediaPlatform }) =>
    handleSocialMediaAction({ action: 'delete', socialMediaPlatform });

  // Generalized connect handler for all platforms
  const handleConnectSocialMedia = useCallback(
    async ({
      socialMediaPlatform,
      getAuthCode,
    }: {
      socialMediaPlatform: SocialMediaPlatform;
      getAuthCode: () => Promise<string | null>;
    }) => {
      try {
        setMutationLoading(true);
        const authCode = await getAuthCode();
        if (!authCode) throw new Error('Authentication failed');
        const response = await connectSocialMedia({
          variables: { authCode, profileId: Number(profileId), socialMediaPlatform },
          update: (cache, { data, errors }) => {
            if (errors) return;
            cache.modify({
              id: cache.identify({ __typename: 'Profile', id: Number(profileId) }),
              fields: {
                socialMedia: () => ({
                  ...existingSocialMedia,
                  id: Number(profileId),
                  __typename: 'SocialMedia',
                  [SOCIAL_MEDIA_FIELD_MAP[socialMediaPlatform].field]: data?.connectSocialMedia,
                }),
              },
            });
          },
        });
        const { isTokenValid } = response?.data?.connectSocialMedia || {};
        if (!isTokenValid) throw new Error('Token is not valid');
        if (heapLocation) {
          captureHeapEvent({
            name: HEAP_EVENT_NAMES.TALENT_SOCIAL_LINK_ADD_SUCCESS,
            data: {
              socialPlatform: socialMediaPlatform,
              timestamp: new Date().toISOString(),
              device: deviceInfo,
              browser: browserInfo,
              location: heapLocation,
            },
          });
        }
        showMessage({
          color: 'success',
          message: t('talent:profileEdit.social.connect.successful', {
            socialMediaPlatform: SOCIAL_MEDIA_FIELD_MAP[socialMediaPlatform].platformName,
          }),
        });
      } catch (error) {
        if (heapLocation) {
          captureHeapEvent({
            name: HEAP_EVENT_NAMES.TALENT_SOCIAL_LINK_ADD_ERROR,
            data: {
              socialPlatform: socialMediaPlatform,
              timestamp: new Date().toISOString(),
              device: deviceInfo,
              browser: browserInfo,
              errorMessage: error?.message,
              location: heapLocation,
            },
          });
        }
        captureExceptionEvent(`${socialMediaPlatform} account connection failed`, {
          location: 'talent > profile > social consent modal',
          profileId,
          accountId,
          artistId,
          errorMessage: error?.message,
          errorDescription: error?.description,
        });
        if (handleConnectSocialMediaError) {
          handleConnectSocialMediaError();
        } else {
          showMessage({ color: 'danger', message: t('talent:profileEdit.social.connect.error') });
        }
      } finally {
        setMutationLoading(false);
      }
    },
    [
      accountId,
      artistId,
      browserInfo,
      connectSocialMedia,
      deviceInfo,
      existingSocialMedia,
      handleConnectSocialMediaError,
      heapLocation,
      profileId,
      showMessage,
      t,
    ]
  );

  const environment = getEnvironment();

  // Instagram connect handler
  const handleInstagramAuth = async () => {
    const clientId: string = environment.INSTAGRAM_CLIENT_ID;
    const ulaUrl = environment.ULA_URL;
    const redirectUri = `${ulaUrl}/instagram-auth/`;
    const url = `https://www.instagram.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=instagram_business_basic`;
    const popup: Window | null = window.open(url, 'instagram-auth', getPopupDimensions());
    await handleConnectSocialMedia({
      socialMediaPlatform: SocialMediaPlatform.Instagram,
      getAuthCode: () => onInstagramAuthCodeReceived(popup),
    });
  };

  // TikTok connect handler
  const handleTikTokAuth = async () => {
    const clientKey: string = environment.TIKTOK_CLIENT_KEY;
    const ulaUrl = environment.ULA_URL;
    const redirectUri = `${ulaUrl}/tiktok-auth/`;
    const params = new URLSearchParams({
      // eslint-disable-next-line camelcase
      client_key: clientKey,
      scope: 'user.info.basic,user.info.stats,user.info.profile',
      // eslint-disable-next-line camelcase
      response_type: 'code',
      // eslint-disable-next-line camelcase
      redirect_uri: redirectUri,
    });
    const url = `https://www.tiktok.com/v2/auth/authorize?${params.toString()}`;
    const popup: Window | null = window.open(url, 'tiktok-auth', getPopupDimensions());
    await handleConnectSocialMedia({
      socialMediaPlatform: SocialMediaPlatform.Tiktok,
      getAuthCode: () => onTikTokAuthCodeReceived(popup),
    });
  };

  const onSuccess = useCallback(
    async (data) => {
      await handleConnectSocialMedia({
        socialMediaPlatform: SocialMediaPlatform.Youtube,
        getAuthCode: () => Promise.resolve(data.code),
      });
    },
    [handleConnectSocialMedia]
  );

  const handleYoutubeAuth = useGoogleLogin({
    // eslint-disable-next-line camelcase
    redirect_uri: environment.BASE_APP_URL,
    onSuccess,
    onError: (error) => {
      showMessage({ color: 'danger', message: error.error_description });
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_SOCIAL_LINK_ADD_ERROR,
        data: {
          socialPlatform: SocialMediaPlatform.Youtube,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
          errorMessage: error?.error_description,
          location: heapLocation,
        },
      });
      captureExceptionEvent(`YouTube account connection failed`, {
        location: 'talent > profile > social consent modal',
        profileId,
        accountId,
        artistId,
        errorMessage: error?.error,
        errorDescription: error?.error_description,
      });
    },
    flow: 'auth-code',
    // eslint-disable-next-line camelcase
    ux_mode: 'popup',
    scope: 'https://www.googleapis.com/auth/youtube.readonly',
  });

  return {
    mutationLoading,
    disconnectSocialAccount,
    deleteAllSocialData,
    handleInstagramAuth,
    handleTikTokAuth,
    handleYoutubeAuth,
    handleConnectSocialMedia,
  };
};

export type UseSocialMediaActionsValues = ReturnType<typeof useSocialMediaActions>;
